interface RiskDisclosureModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function RiskDisclosureModal({ isOpen, onClose }: RiskDisclosureModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative w-full sm:max-w-[600px] max-h-[85vh] bg-bg-page-body rounded-t-[16px] sm:rounded-[16px] shadow-xl flex flex-col overflow-hidden mx-0 sm:mx-[16px]">
        <div className="flex items-center justify-between px-[16px] py-[14px] border-b border-border-subtle shrink-0">
          <h2 className="text-text-base text-[16px] font-bold">
            Risk Disclosure
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="material-symbols-outlined text-[22px] text-text-subtle"
          >
            close
          </button>
        </div>

        <div className="px-[16px] py-[16px] overflow-y-auto">
          <div className="text-text-base space-y-4">
            <div>
              <h3 className="text-[14px] font-bold mb-2">RISK DISCLOSURE</h3>
              <p className="text-[11px] text-text-subtle mb-1">
                On-Ramp, Off-Ramp and Transfer Services
              </p>
              <p className="text-[11px] text-text-subtle mb-2">
                Effective Date: 1 July 2026
                <br />
                Last Updated: 30 September 2026
              </p>
            </div>

            <div className="space-y-4 text-[12px] leading-relaxed">
              <p>
                This Risk Disclosure applies to the use of fiat-to-virtual
                currency (&ldquo;On-Ramp&rdquo;), virtual currency-to-fiat
                (&ldquo;Off-Ramp&rdquo;), and virtual currency transfer services
                (&ldquo;Transfer Services&rdquo;) provided through the
                infrastructure of <strong>TYLT FINTECH INC.</strong>{" "}
                (&ldquo;Tylt&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or
                &ldquo;our&rdquo;).
              </p>
              <p>
                TYLT FINTECH INC. is a Canadian Money Services Business
                registered with the Financial Transactions and Reports Analysis
                Centre of Canada (&ldquo;FINTRAC&rdquo;) under MSB Registration
                No. C10001670.
              </p>
              <p>
                Users may access Tylt&rsquo;s Services directly through
                Tylt&rsquo;s website and exchange interface, including
                exchange.tylt.money, or through approved integrators, partners,
                affiliates, platforms, applications, or other third-party
                integrations.
              </p>
              <p>
                By using Tylt&rsquo;s Services, you acknowledge and accept the
                risks described below.
              </p>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  1. Scope of Services
                </h4>
                <p>Tylt may provide infrastructure enabling:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>
                    On-Ramp: conversion of fiat currency into virtual currency /
                    crypto-assets;
                  </li>
                  <li>
                    Off-Ramp: conversion of virtual currency / crypto-assets into
                    fiat currency; and
                  </li>
                  <li>
                    Transfer Services: transfer, movement, crediting, debiting,
                    or settlement of virtual currency / crypto-assets between
                    supported wallets, accounts, ledger balances, self-hosted
                    wallets, custodial wallets, User-designated wallets, or other
                    approved virtual currency destinations.
                  </li>
                </ul>
                <p className="mt-2">
                  The availability of any particular Service, payment method,
                  transfer function, wallet type, asset, or destination may
                  depend on product availability, User eligibility, jurisdiction,
                  regulatory restrictions, compliance requirements, and
                  Tylt&rsquo;s risk controls.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  2. Virtual Currency Risk
                </h4>
                <p>
                  Virtual currency / crypto-assets, including stablecoins such as
                  USDT and USDC, involve significant risk. Value may fluctuate
                  because of market conditions, liquidity, issuer risk, blockchain
                  risk, regulatory developments, cybersecurity incidents,
                  counterparty failures, or other external factors.
                </p>
                <p className="mt-2">
                  Tylt does not guarantee the value, liquidity, redemption,
                  convertibility, or future availability of any virtual currency /
                  crypto-asset.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  3. Stablecoin Risk
                </h4>
                <p>
                  Stablecoins are intended to maintain a reference value but may
                  lose their peg, become illiquid, be suspended, be frozen by an
                  issuer, become unavailable for redemption, or be affected by
                  regulatory, market, operational, reserve, or issuer-specific
                  events.
                </p>
                <p className="mt-2">
                  Tylt is not the issuer of any stablecoin and does not control
                  issuer reserves, redemption processes, freeze functions, smart
                  contracts, token administration, issuer policies, or issuer
                  solvency.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  4. Blockchain Transaction Risk
                </h4>
                <p>
                  Virtual currency transactions may be irreversible once broadcast
                  or confirmed on-chain. Users are responsible for ensuring that
                  wallet addresses, the blockchain network, destination support,
                  beneficiary details, and transaction instructions are correct.
                </p>
                <p className="mt-2">
                  If virtual currency is sent to the wrong address, the wrong
                  network, an incompatible wallet, a sanctioned, frozen or
                  blacklisted wallet, an unsupported blockchain, or another
                  incorrect destination, recovery may not be possible.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  5. Transfer Service Risk
                </h4>
                <p>
                  Transfer Services involve the movement of virtual currency /
                  crypto-assets between wallets, accounts, ledger balances, or
                  User-designated destinations. Transfers may be executed on-chain,
                  internally through Tylt&rsquo;s ledger, through a custodial
                  wallet, through an approved integration, or through a
                  combination of those methods.
                </p>
                <p className="mt-2">
                  The User is responsible for confirming the transfer destination,
                  wallet address, blockchain network, beneficiary, account
                  reference, and transaction purpose before submitting or
                  confirming a transfer. A destination provided through an
                  integration should only be used where it has been selected,
                  specified, authorised, or otherwise confirmed by the User.
                </p>
                <p className="mt-2">
                  Tylt may not be able to reverse, recall, cancel, or recover a
                  transfer once it has been executed, credited, debited,
                  broadcast, or confirmed. Availability may also depend on
                  jurisdiction, product availability, sanctions, Travel Rule,
                  wallet screening, blockchain analytics, and Tylt&rsquo;s
                  compliance and risk controls.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  6. Wrong Wallet, Wrong Network and Wrong Beneficiary Risk
                </h4>
                <p>
                  Transfers may be permanently lost where the User enters an
                  incorrect wallet address, selects the wrong blockchain network,
                  uses a destination that does not support the token or chain,
                  transfers to a scammer or unauthorised beneficiary, transfers to
                  a sanctioned, frozen, blacklisted or high-risk wallet, transfers
                  to a smart contract, exchange deposit or custodial address that
                  cannot properly credit the funds, or provides an incorrect
                  account, customer, beneficiary or transaction reference.
                </p>
                <p className="mt-2">
                  Tylt is not responsible for losses arising from incorrect User
                  instructions, incorrect wallet details, wrong-chain transfers,
                  unsupported wallet destinations, or unauthorised beneficiaries
                  except where directly caused by Tylt&rsquo;s failure to execute
                  a valid instruction correctly.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  7. Internal Ledger Transfer Risk
                </h4>
                <p>
                  Where transfers are processed through Tylt&rsquo;s internal
                  ledger, credits and debits may not correspond to an immediate
                  on-chain movement. Internal transfers may be subject to
                  reconciliation, compliance review, liquidity checks, and
                  operational controls. Tylt may delay, suspend, reverse, or
                  adjust an internal ledger entry where required to correct an
                  error, address fraud, comply with law, or satisfy regulatory or
                  risk requirements. A ledger balance may be restricted where
                  suspicious activity, sanctions exposure, wallet risk, account
                  compromise, or payment-integrity concerns are identified.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  8. Custodial and Self-Hosted Wallet Risk
                </h4>
                <p>
                  Transfers to or from self-hosted or unhosted wallets involve
                  risks including loss of private keys, compromised wallets,
                  incorrect addresses, malware, phishing, theft, and inability to
                  recover funds. Transfers to or from custodial wallets involve
                  risks including custodian failure, account freezes, withdrawal
                  restrictions, compliance holds, platform outages, insolvency, or
                  third-party custody controls.
                </p>
                <p className="mt-2">
                  Tylt may restrict, delay, or refuse transfers to or from
                  particular wallet types, including self-hosted wallets,
                  custodial wallets, exchange wallets, or high-risk wallet
                  clusters, based on compliance, sanctions, AML/ATF, Travel Rule,
                  fraud, or other risk considerations.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  9. Network and Gas Fee Risk
                </h4>
                <p>
                  Blockchain transactions may be delayed or fail due to network
                  congestion, insufficient gas fees, validator issues, chain
                  disruptions, blockchain outages, smart contract failures,
                  protocol failures, or other technical conditions. Network,
                  miner, validator, and gas fees may fluctuate materially and may
                  be deducted from the transaction amount or charged separately.
                </p>
                <p className="mt-2">
                  Tylt does not control blockchain networks and is not responsible
                  for network delays, failed confirmations, chain disruptions, or
                  changes in network fees.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  10. Exchange Rate and Price Risk
                </h4>
                <p>
                  Exchange rates are dynamic and may change before a transaction
                  is completed. The amount of virtual currency delivered,
                  transferred, credited, or debited, or the amount of fiat
                  currency received, may differ from an indicative quote due to
                  market movement, liquidity, spread, fees, compliance delays,
                  payment delays, or third-party processing timelines.
                </p>
                <p className="mt-2">
                  Tylt does not guarantee that an indicative rate will remain
                  available unless expressly confirmed for a specific transaction.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  11. Liquidity Risk
                </h4>
                <p>
                  Virtual currency and fiat liquidity may vary depending on market
                  conditions, payment corridors, banking availability,
                  liquidity-provider availability, wallet infrastructure, asset
                  availability, and transaction size. A transaction or transfer may
                  be delayed, rejected, partially processed, or executed at a
                  different rate where liquidity is limited or unavailable.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  12. Fiat Payment and Banking Risk
                </h4>
                <p>
                  Fiat payments depend on payment service providers, financial
                  institutions, banking partners, payment processors, local payment
                  rails, and clearing systems. Payments may be delayed, rejected,
                  reversed, frozen, returned, or placed under review because of
                  bank processing rules, payment-provider controls, incorrect
                  account information, name mismatches, compliance checks, banking
                  holidays, payment-rail outages, fraud controls, or third-party
                  failures.
                </p>
                <p className="mt-2">
                  Tylt does not control independent banking or payment systems and
                  is not responsible for delays or failures caused by such third
                  parties except to the extent required by applicable law.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  13. Name Matching and Third-Party Payment Risk
                </h4>
                <p>
                  Where applicable, the name provided during the transaction must
                  correspond with the verified identity associated with the
                  originating or beneficiary payment account. Transactions
                  involving name mismatches, unauthorised third-party payments,
                  unauthorised payment accounts, inconsistent beneficiary
                  information, or other payment-integrity concerns may be delayed,
                  rejected, reversed, or refunded where possible.
                </p>
                <p className="mt-2">
                  Where a return or refund is possible, it may be subject to
                  administrative fees, investigation fees, bank charges,
                  exchange-rate differences, processing costs, or other applicable
                  deductions. A processing, administrative, return, or
                  investigation fee of up to 5% of the transaction amount may
                  apply where disclosed or otherwise permitted under applicable
                  law.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  14. Compliance and Regulatory Risk
                </h4>
                <p>
                  Transactions and transfers may be subject to know-your-customer
                  checks, business verification, sanctions screening, blockchain
                  analytics, Travel Rule checks, AML/ATF monitoring, fraud review,
                  source-of-funds, source-of-wealth and source-of-virtual-currency
                  review, and other compliance controls.
                </p>
                <p className="mt-2">
                  Tylt may delay, reject, suspend, freeze, cancel, restrict, or
                  report a transaction or transfer where required or permitted by
                  applicable law, regulation, sanctions, payment-provider or
                  banking-partner requirements, or Tylt&rsquo;s compliance and risk
                  framework. Tylt may request additional information or
                  documentation before processing or completing a transaction or
                  transfer. A User or transaction being approved previously does
                  not guarantee approval of subsequent transactions.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  15. Sanctions and Wallet Screening Risk
                </h4>
                <p>
                  Tylt may screen users, businesses, counterparties, wallets,
                  transactions, payment accounts, transfer destinations,
                  beneficiaries, and related persons against applicable sanctions
                  lists, blockchain analytics systems, fraud indicators, adverse
                  media sources, and internal risk systems.
                </p>
                <p className="mt-2">
                  Transactions or transfers involving sanctioned persons,
                  jurisdictions or wallets, high-risk wallets, darknet markets,
                  mixers, tumblers, ransomware, fraud, scams, terrorist financing,
                  or other prohibited activity may be rejected, restricted,
                  suspended, frozen, or reported where required or permitted by
                  law.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  16. Travel Rule Risk
                </h4>
                <p>
                  Where applicable, Tylt may be required to collect, verify,
                  transmit, receive, request, or retain originator and beneficiary
                  information in connection with virtual currency transfers,
                  including information about the sender, recipient, wallet owner,
                  beneficiary, account holder, transaction reference, wallet
                  address, address, date of birth, country of residence, customer
                  identification, counterparty virtual asset service provider, and
                  transaction purpose.
                </p>
                <p className="mt-2">
                  A transaction or transfer may be delayed or rejected where
                  required information is missing, incomplete, inaccurate,
                  unverifiable, inconsistent with the transaction, or not
                  supported by the sending or receiving counterparty.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  17. Counterparty and Beneficiary Risk
                </h4>
                <p>
                  Users are responsible for verifying the legitimacy, identity,
                  wallet details, and payment instructions of any counterparty,
                  beneficiary, recipient, or other destination. Tylt is not
                  responsible for losses caused by fake or fraudulent businesses,
                  scam beneficiaries, incorrect recipient details, impersonation,
                  business email compromise, fraudulent wallet substitution,
                  unauthorised payment instructions, or disputes between the User
                  and a beneficiary, recipient, integrator, partner, platform, or
                  other third party.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  18. Fraud, Scam and Cybersecurity Risk
                </h4>
                <p>
                  Virtual currency transactions and transfers are frequently
                  targeted by fraud, phishing, impersonation, investment scams,
                  romance scams, account takeover, malware, fake platforms, social
                  engineering, and other forms of cyber-enabled fraud.
                </p>
                <p className="mt-2">
                  Users are responsible for verifying the legitimacy of any wallet
                  address, counterparty, payment instruction, transfer destination,
                  platform, or transaction purpose before proceeding. Tylt is not
                  liable for losses arising from fraud, scams, phishing,
                  compromised devices, compromised accounts, unauthorised access,
                  or incorrect instructions not caused by Tylt.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  19. Integrator and Third-Party Risk
                </h4>
                <p>
                  Users may access Tylt&rsquo;s Services through approved
                  integrators, partners, affiliates, platforms, applications, or
                  other third-party integrations. Such third parties may provide
                  separate products, interfaces, services, representations, or
                  contractual terms.
                </p>
                <p className="mt-2">
                  Tylt is not responsible for goods or services supplied by third
                  parties, representations made independently by third parties,
                  third-party delivery or refund obligations, third-party
                  disputes, chargebacks relating to third-party goods or services,
                  or the independent conduct of an integrator, partner, affiliate,
                  platform, beneficiary, or other third party. Any dispute
                  concerning goods or services provided independently by a third
                  party should be resolved directly with the relevant third party.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  20. Geographic and Eligibility Risk
                </h4>
                <p>
                  Tylt&rsquo;s Services are not available in every jurisdiction.
                  Availability may depend on the User&rsquo;s country of
                  residence, domicile, current location, place of establishment,
                  product, payment method, payment rail, wallet type, asset,
                  counterparty, and applicable regulatory requirements.
                </p>
                <p className="mt-2">
                  Users must not attempt to circumvent geographic or eligibility
                  restrictions by using false addresses, false entity information,
                  VPNs, proxy services, location-spoofing technologies, or other
                  methods intended to conceal or misrepresent location.
                </p>
                <p className="mt-2">
                  Certain Services, including particular Open Banking payment
                  methods, may be unavailable to individuals residing in,
                  domiciled in, or accessing the Service from the European Union,
                  European Economic Area (&ldquo;EU/EEA&rdquo;) or United Kingdom
                  (&ldquo;UK&rdquo;), and to businesses or other legal entities
                  established in those jurisdictions. Inaccurate, incomplete, or
                  misleading eligibility or access information may result in the
                  relevant transaction or Service being declined, suspended,
                  cancelled, or restricted.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  21. No Investment Advice
                </h4>
                <p>
                  Tylt does not provide investment, trading, legal, tax,
                  financial, accounting, or regulatory advice. Any decision to buy,
                  sell, receive, send, transfer, hold, or convert virtual currency
                  / crypto-assets is made solely by the User. Users should obtain
                  independent professional advice where appropriate.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">22. Tax Risk</h4>
                <p>
                  Virtual currency transactions and transfers may have tax
                  consequences. Users are responsible for determining applicable
                  tax treatment, reporting obligations, tax liabilities, duties,
                  and other charges arising from their transactions or transfers.
                  Tylt does not provide tax advice and is not responsible for a
                  User&rsquo;s tax obligations.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  23. Regulatory Change Risk
                </h4>
                <p>
                  Virtual currency laws, payment laws, AML/ATF requirements,
                  sanctions requirements, Travel Rule requirements, tax laws, and
                  regulatory expectations may change. Such changes may affect
                  availability, pricing, timing, execution, transferability,
                  reporting, supported jurisdictions, supported payment methods, or
                  the legality of particular Services or transactions.
                </p>
                <p className="mt-2">
                  Tylt may modify, suspend, restrict, or discontinue Services
                  where required or appropriate due to legal, regulatory,
                  compliance, operational, or risk considerations.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  24. Technology and Operational Risk
                </h4>
                <p>
                  Tylt&rsquo;s Services may be affected by system outages,
                  scheduled or unscheduled maintenance, cyber incidents, API
                  failures, wallet-infrastructure failures, ledger issues, internet
                  disruptions, vendor failures, payment-system outages, software
                  defects, or other operational events. Tylt does not guarantee
                  uninterrupted, error-free, or continuous availability of its
                  Services.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  25. Transaction and Transfer Finality
                </h4>
                <p>
                  Once a virtual currency transaction or transfer has been
                  executed, broadcast, credited, debited, or confirmed on-chain, it
                  may not be reversible. Internal ledger transfers may also be
                  treated as final once credited or debited, subject to error
                  correction, fraud review, compliance review, legal obligations,
                  and Tylt&rsquo;s applicable controls.
                </p>
                <p className="mt-2">
                  Refunds, recalls, reversals, or recovery attempts are not
                  guaranteed. Where one is possible, the amount returned may be
                  reduced by fees, spreads, exchange-rate differences, network
                  costs, bank charges, payment-provider fees, investigation costs,
                  or administrative deductions.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  26. User Responsibility
                </h4>
                <p>
                  By proceeding with an On-Ramp, Off-Ramp, or Transfer Service,
                  the User confirms that they understand and accept the risks
                  associated with such Services. The User is responsible for
                  reviewing all transaction details before confirmation, including,
                  where applicable, amount, currency, virtual currency /
                  crypto-asset, wallet address, blockchain network, beneficiary
                  details, fees, exchange rate, transfer destination, account
                  reference, and transaction purpose.
                </p>
                <p className="mt-2">
                  By confirming a transaction, the User confirms that the
                  transaction details and instructions are correct and that the
                  User wishes to proceed.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">27. Contact</h4>
                <p>
                  <strong>TYLT FINTECH INC.</strong>
                </p>
                <p className="mt-2">Registered Address:</p>
                <p>2-1130 HACHEY AVE</p>
                <p>COQUITLAM, BC</p>
                <p>V3K 2H4</p>
                <p>CANADA</p>
                <p className="mt-2">Company Registration No.: BC1571749</p>
                <p>FINTRAC MSB Registration No.: C10001670</p>
                <p>
                  Email:{" "}
                  <a
                    href="mailto:compliance@tylt.money"
                    className="text-icon-blue-dark underline"
                  >
                    compliance@tylt.money
                  </a>
                </p>
                <p>
                  Website:{" "}
                  <a
                    href="https://www.tylt.money"
                    className="text-icon-blue-dark underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    www.tylt.money
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RiskDisclosureModal;
